import React from 'react';
import PropTypes from 'prop-types';
import ReactWaves, { Regions } from '@dschoon/react-waves';
import global from '../../stores/GlobalStore';

import {
  calculateZoom,
  formatDuration,
  formatDurationMillis,
  getFakePeaks,
  getRandomInt,
} from '../../utils/podcastUtils';
import LoadingSpinner from '../Shared/LoadingSpinner';

import './styles/ClippPlayer.scss';

const START = 20;

export default class AudioClipper extends React.Component {
  constructor(props) {
    super(props);

    this.audioPeaks = this.getAudioPeaks();
    this.state = this.getInitialState(getRandomInt(1000));

    this.clippNameRef = React.createRef();
  }

  componentWillUpdate(nextProps, nextState, nextContext) {
    if (nextProps.episode !== this.props.episode) {
      this.setState({ regions: null, wavesurfer: null }, () => {
        this.setState(this.getInitialState(getRandomInt(1000)));
      });
    }
  }

  componentDidCatch(error, info) {
    console.log(info);
    console.error(error);
  }

  getInitialState = regionId => {
    return {
      loaded: false,
      wavesurfer: null,
      playing: false,
      pos: 0,
      currentFormatTime: formatDuration(START, true),
      activeRegion: regionId,
      regions: {
        [regionId]: {
          id: regionId,
          start: START,
          end: 80,
          color: 'rgba(100, 149, 240, 0.5)',
        },
      },
    };
  };

  onReady = ({ wavesurfer }) => {
    this.setState({ loaded: true, wavesurfer }, () => {
      this.state.wavesurfer.seekTo(this.secondsToPosition(START));
    });
  };

  onLoading = ({ wavesurfer, originalArgs = [] }) => {
    this.setState({ loaded: originalArgs[0] === 100, wavesurfer });
  };

  onPosChange = (pos, wavesurfer) => {
    if (pos !== this.state.pos) {
      this.setState({ wavesurfer });
    }
  };

  audioProcess = e => {
    const currentTime = Math.ceil(e.originalArgs && e.originalArgs[0]);
    const wavesurfer = e.wavesurfer;

    if (this.endTime && currentTime > this.endTime) {
      this.setState({ isPlaying: false });
    } else if (currentTime && Number.isInteger(currentTime) && currentTime !== this.state.pos) {
      this.setState({
        currentFormatTime: formatDuration(currentTime - 1, true),
        totalDuration: wavesurfer.getDuration() + 1,
        pos: currentTime,
        wavesurfer: wavesurfer,
      });
    }
  };

  secondsToPosition = sec => {
    return (1 / this.state.wavesurfer.getDuration()) * sec;
  };

  renderCloseBtn = () => {
    return (
      <div className='clipp-action-btn close' onClick={this.props.toggleAudioClipper}>
        <i className='far fa-times' />
      </div>
    );
  };

  zoom = direction => {
    const { wavesurfer } = this.state;
    const currentZoom = wavesurfer.params.minPxPerSec;

    if (direction === 'in' && currentZoom <= 3) {
      wavesurfer.zoom(currentZoom + 0.25);
    } else if (direction === 'out' && currentZoom > 0.25) {
      wavesurfer.zoom(currentZoom - 0.25);
    }
  };

  handleSingleRegionUpdate = e => {
    this.setState({ playing: false }, () => {
      const newState = Object.assign(this.state, {
        currentFormatTime: formatDuration(e.region.start, true),
        regions: {
          [e.region.id]: e.region,
        },
      });

      if (this.state.wavesurfer) {
        this.state.wavesurfer.seekTo(this.secondsToPosition(e.region.start));
      }

      this.setState(newState);
    });
  };

  handleRegionClick = e => {
    this.setState({ playing: false });
  };

  getAudioPeaks = () => {
    if (!this.props.corsError && this.props.episode.audioPeaks) {
      return this.props.episode.audioPeaks;
    } else {
      if (this.props.isMobile) {
        return getFakePeaks(0.1, 0.1);
      } else {
        return getFakePeaks(0.1, 0.4);
      }
    }
  };

  startRegion = () => {
    this.state.wavesurfer.seekTo(this.secondsToPosition(this.state.regions[this.state.activeRegion].start));
    this.setState({ playing: true });
  };

  stopRegion = () => {
    this.setState({ playing: false }, () => {
      this.state.wavesurfer.seekTo(this.secondsToPosition(this.state.regions[this.state.activeRegion].start));
    });
  };

  createClipp = () => {
    const { show, episode } = this.props;

    global()
      .getPodcastStore()
      .createClipp(
        {
          start: this.state.regions[this.state.activeRegion].start,
          end: this.state.regions[this.state.activeRegion].end,
          input: this.props.audioDetails.audioUrl,
          output: `${this.clippNameRef.current.value}`,
          title: this.clippNameRef.current.value,
          imageUrl: episode.imageUrl || show.imageUrl,
          showId: show.id,
          episodeId: episode.id,
        },
        result => {
          console.log(result);
          this.setState({ showModal: false });
        }
      );
  };

  toggleModal = () => {
    this.setState({ showModal: !this.state.showModal });
  };

  renderCreateModal = () => {
    return (
      <div className='modal-back'>
        <div className='create-clipp-modal'>
          <div className='inner-container'>
            <div className='modal-title'>Create Clipp</div>
            <input className='clipp-name-input' ref={this.clippNameRef} placeholder='Enter Clipp Title' />

            <div className='action-btns'>
              <button onClick={this.toggleModal}>Cancel</button>
              <button onClick={this.createClipp}>Create</button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  render() {
    const activeRegion = this.state.regions ? this.state.regions[this.state.activeRegion] : {};
    const { showModal } = this.state;

    return (
      <div className='audio-clipper'>
        {showModal && this.renderCreateModal()}
        <div className='react-wave-container'>
          <ReactWaves
            audioFile={this.props.audioDetails.audioUrl}
            className={'react-waves'}
            audioPeaks={this.audioPeaks}
            options={{
              autoCenter: false,
              backend: 'MediaElement',
              barGap: 2,
              barWidth: 1.8,
              barHeight: 2,
              cursorWidth: 2,
              cursorColor: 'rgb(232, 80, 158)',
              height: 80,
              fillParent: true,
              hideScrollbar: false,
              progressColor: '#D1D6DA',
              responsive: true,
              waveColor: '#D1D6DA',
            }}
            volume={1}
            zoom={calculateZoom(this.props.episode.duration)}
            playing={this.state.playing}
            onPosChange={this.onPosChange}
            onLoading={this.onLoading}
            onReady={this.onReady}
            onAudioprocess={this.audioProcess}
          >
            <Regions
              onSingleRegionUpdate={this.handleSingleRegionUpdate}
              onRegionClick={this.handleRegionClick}
              onRegionOut={this.stopRegion}
              regions={this.state.regions}
            />
          </ReactWaves>
          {this.renderCloseBtn()}
        </div>
        <div className='audio-clipper-controls'>
          <div style={{ width: '45px' }}>
            <button className='clipp-control-btn' onClick={this.state.playing ? this.stopRegion : this.startRegion}>
              {this.state.isLoading ? (
                <LoadingSpinner />
              ) : (
                <i className={'fa ' + (this.state.playing ? 'fa-pause' : 'fa-play')} />
              )}
            </button>
          </div>
          <div className='audio-current-time'>{this.state.currentFormatTime}</div>
          <div className='clipp-durations'>
            <span>
              <i className='far fa-plus-circle' onClick={this.zoom.bind(this, 'in')} />
            </span>
            <span>
              <i className='far fa-minus-circle' onClick={this.zoom.bind(this, 'out')} />
            </span>
            <div>Start: {formatDurationMillis(activeRegion.start)}</div>
            <div>End: {formatDurationMillis(activeRegion.end)}</div>
            <div>Duration: {formatDurationMillis(activeRegion.end - activeRegion.start)}</div>
            <button className='clipp-btn' onClick={this.toggleModal}>
              Create Clipp
            </button>
          </div>
        </div>
      </div>
    );
  }
}

AudioClipper.propTypes = {
  isMobile: PropTypes.bool,
  darkMode: PropTypes.bool,
  audioDetails: PropTypes.object,
  episode: PropTypes.object,
  show: PropTypes.object,
  options: PropTypes.object,
  toggleAudioClipper: PropTypes.func,
};

AudioClipper.defaultProps = {
  isMobile: false,
  darkMode: false,
  audioDetails: {},
  episode: {},
  show: {},
  options: {},
};
